import { orderedModules } from './module-tree';
import { isCampoLote } from './lote-uso';

interface CropInfo { cropId?: string; cropName?: string; cultivo?: string; cropCycleStatus?: 'OPEN' | 'CLOSED'; cropCycleLabel?: string }
interface LoteLike extends CropInfo { loteId: string; name: string; uso?: 'CAMPO' | 'ADMINISTRATIVO' }
interface ModuleLike extends CropInfo {
  status?: string;
  moduleId: string;
  moduleCode: string;
  name?: string;
  loteId: string;
  parentModuleId?: string;
}

/** Selección del árbol: un lote, o una zona/subzona (que pertenece a un lote). */
export interface LoteZoneSelection { loteId: string; moduleId?: string }

/** Textos y catálogo para armar la etiqueta de cultivo; sin ellos las opciones salen sin etiqueta. */
export interface ZoneTagContext {
  cropNames: Map<string, string>;
  statusLabels: Record<string, string>;
  closedLabel: string;
}

export interface LoteZoneOption { id: string; label: string; depth: number; name: string; tag?: { text: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } }

/** Etiqueta "Cultivo · Ciclo" con color según el estado; undefined si la zona no tiene cultivo. */
function cropTag(item: CropInfo & { status?: string }, ctx?: ZoneTagContext): LoteZoneOption['tag'] {
  if (!ctx) return undefined;
  const crop = (item.cropId && ctx.cropNames.get(item.cropId)) || item.cropName || item.cultivo;
  if (!crop) return undefined;
  const parts = [crop];
  if (item.cropCycleLabel) parts.push(item.cropCycleLabel);
  if (item.cropCycleStatus === 'CLOSED') return { text: `${parts.join(' · ')} · ${ctx.closedLabel}`, tone: 'neutral' };
  const st = item.status;
  if (st === 'CRITICO') return { text: `${parts.join(' · ')} · ${ctx.statusLabels.CRITICO ?? st}`, tone: 'danger' };
  if (st === 'ATENCION' || st === 'COSECHANDO') return { text: `${parts.join(' · ')} · ${ctx.statusLabels[st] ?? st}`, tone: 'warning' };
  return { text: parts.join(' · '), tone: 'success' };
}

const LOTE_PREFIX = 'L:';
const MODULE_PREFIX = 'M:';

/**
 * Opciones planas del árbol lote > zona > subzona para `SearchableSelect`.
 * Cada lote aparece primero y sus zonas y subzonas debajo, con su ruta en `label` y su nivel en `depth` (para sangrar en la lista).
 * Los ids llevan prefijo para no chocar entre lote y zona.
 * `campoOnly` omite los lotes ADMINISTRATIVO y sus zonas (historial); por defecto se listan todos (asistencia).
 */
export function loteZoneOptions(lotes: LoteLike[], modules: ModuleLike[], tagCtx?: ZoneTagContext, opts?: { campoOnly?: boolean }): LoteZoneOption[] {
  const out: LoteZoneOption[] = [];
  const withTag = (o: LoteZoneOption, tag: LoteZoneOption['tag']): LoteZoneOption => (tag ? { ...o, tag } : o);
  for (const lote of lotes) {
    if (opts?.campoOnly && !isCampoLote(lote)) continue; // historial: sin lotes administrativos
    out.push(withTag({ id: `${LOTE_PREFIX}${lote.loteId}`, label: lote.name, depth: 0, name: lote.name }, cropTag(lote, tagCtx)));
    const loteModules = modules.filter(m => m.loteId === lote.loteId);
    const byId = new Map(loteModules.map(m => [m.moduleId, m]));
    const path = (m: ModuleLike): string => {
      const names: string[] = [];
      let cur: ModuleLike | undefined = m;
      while (cur) {
        names.unshift(cur.name || cur.moduleCode);
        cur = cur.parentModuleId ? byId.get(cur.parentModuleId) : undefined;
      }
      return names.join(' › ');
    };
    for (const { mod, depth } of orderedModules(loteModules)) {
      out.push(withTag({ id: `${MODULE_PREFIX}${mod.moduleId}`, label: `${lote.name} › ${path(mod)}`, depth: depth + 1, name: mod.name || mod.moduleCode }, cropTag(mod, tagCtx)));
    }
  }
  return out;
}

/** Interpreta el id elegido en `loteZoneOptions`. Devuelve null si el id no es válido. */
export function parseLoteZoneId(id: string, modules: ModuleLike[]): LoteZoneSelection | null {
  if (id.startsWith(LOTE_PREFIX)) return { loteId: id.slice(LOTE_PREFIX.length) };
  if (id.startsWith(MODULE_PREFIX)) {
    const moduleId = id.slice(MODULE_PREFIX.length);
    const m = modules.find(x => x.moduleId === moduleId);
    return m ? { loteId: m.loteId, moduleId } : null;
  }
  return null;
}

/** Ids de una zona y de todas sus subzonas (mismo lote). Sirve para filtrar tareas por zona. */
export function zoneWithDescendantIds(moduleId: string, modules: Pick<ModuleLike, 'moduleId' | 'parentModuleId'>[]): Set<string> {
  const out = new Set<string>([moduleId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const m of modules) {
      if (m.parentModuleId && out.has(m.parentModuleId) && !out.has(m.moduleId)) { out.add(m.moduleId); grew = true; }
    }
  }
  return out;
}

/** Id del árbol que corresponde a una selección (para mostrarla en el selector). */
export function loteZoneId(sel: LoteZoneSelection): string {
  return sel.moduleId ? `${MODULE_PREFIX}${sel.moduleId}` : `${LOTE_PREFIX}${sel.loteId}`;
}
